import type { TableColumn, TableColumnWidths } from '@ipfabric/utils';
import { maybePropN, prop } from '@ipfabric/utils/fp/prop';
import {
  getColumnDefaultWidth,
  getColumnWidth,
} from 'components/Table/helpers/getColumnWidth';
import type { TableMergedState } from 'components/Table/model/TableModel';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import {
  TABLE_COLUMN_DEFAULT_WIDTH,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_CONTENT_BORDER_WIDTH,
  TABLE_RIGHT_HIDDEN_WIDTH,
} from 'components/Table/tableSettings';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

type EqualWidthColumnPreparation<TRow> = {
  widths: TableColumnWidths;
  equalWidthColumns: TableColumn<TRow>[];
  reservedWidth: number;
};

const assignColumnWidth = <TRow,>(
  widths: TableColumnWidths,
  column: TableColumn<TRow>,
  width: number,
): void => {
  widths[column.key] = width;
};

const assignUniformWidth = <TRow,>(
  widths: TableColumnWidths,
  columns: TableColumn<TRow>[],
  width: number,
): TableColumnWidths => {
  for (const column of columns) {
    assignColumnWidth(widths, column, width);
  }

  return widths;
};

const getConfiguredColumnWidths = <TRow,>(
  columns: TableColumn<TRow>[],
  storedColumnWidths: TableColumnWidths | undefined,
): TableColumnWidths => {
  const widths: TableColumnWidths = {};
  const resolveColumnWidth = getColumnWidth(storedColumnWidths);

  for (const column of columns) {
    assignColumnWidth(widths, column, resolveColumnWidth(column));
  }

  return widths;
};

const prepareEqualWidthColumns = <TRow,>(
  columns: TableColumn<TRow>[],
  storedColumnWidths: TableColumnWidths | undefined,
): EqualWidthColumnPreparation<TRow> => {
  const widths: TableColumnWidths = {};
  const equalWidthColumns: TableColumn<TRow>[] = [];
  let reservedWidth = 0;

  for (const column of columns) {
    const resolvedWidth = column.fixedWidth
      ? getColumnDefaultWidth(column)
      : storedColumnWidths?.[column.key];

    if (resolvedWidth === undefined) {
      equalWidthColumns.push(column);
      continue;
    }

    assignColumnWidth(widths, column, resolvedWidth);
    reservedWidth += resolvedWidth;
  }

  return {
    widths,
    equalWidthColumns,
    reservedWidth,
  };
};

const getAvailableEqualWidthSpace = (
  tableContainerWidth: number,
  reservedWidth: number,
): number =>
  tableContainerWidth -
  reservedWidth +
  TABLE_CONTENT_BORDER_WIDTH +
  TABLE_RIGHT_HIDDEN_WIDTH;

const assignDistributedWidths = <TRow,>(
  widths: TableColumnWidths,
  columns: TableColumn<TRow>[],
  availableWidth: number,
): TableColumnWidths => {
  const baseWidth = Math.floor(availableWidth / columns.length);
  const remainder = availableWidth % columns.length;

  columns.forEach((column, index) => {
    assignColumnWidth(widths, column, baseWidth + (index < remainder ? 1 : 0));
  });

  return widths;
};

const getEqualWidthColumnWidths = <TRow,>(
  columns: TableColumn<TRow>[],
  storedColumnWidths: TableColumnWidths | undefined,
  tableContainerWidth: number | null,
): TableColumnWidths => {
  const { widths, equalWidthColumns, reservedWidth } = prepareEqualWidthColumns(
    columns,
    storedColumnWidths,
  );

  if (equalWidthColumns.length === 0) {
    return widths;
  }

  if (tableContainerWidth === null || tableContainerWidth <= 0) {
    return assignUniformWidth(
      widths,
      equalWidthColumns,
      TABLE_COLUMN_DEFAULT_WIDTH,
    );
  }

  const availableEqualWidthSpace = getAvailableEqualWidthSpace(
    tableContainerWidth,
    reservedWidth,
  );

  if (
    availableEqualWidthSpace <=
    equalWidthColumns.length * TABLE_COLUMN_MIN_WIDTH
  ) {
    return assignUniformWidth(
      widths,
      equalWidthColumns,
      TABLE_COLUMN_MIN_WIDTH,
    );
  }

  return assignDistributedWidths(
    widths,
    equalWidthColumns,
    availableEqualWidthSpace,
  );
};

export const selectRenderedColumnWidths: (
  s: TableMergedState,
) => TableColumnWidths = createScopedSelector(() =>
  createSelector(
    selectVisibleColumns,
    prop('settings', 'columnWidth'),
    maybePropN('tableContainerSize', 'width'),
    prop('equalWidthColumns'),
    (columns, storedColumnWidths, tableContainerWidth, useEqualWidthColumns) =>
      useEqualWidthColumns
        ? getEqualWidthColumnWidths(
            columns,
            storedColumnWidths,
            tableContainerWidth,
          )
        : getConfiguredColumnWidths(columns, storedColumnWidths),
  ),
);
